import React, { useState, useRef } from 'react';
import { useAdaptivePoll } from '@shared/hooks/useAdaptivePoll';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { formatBytes } from '@/shared/utils/formatBytes';
import { logger } from '@shared/utils/logger';
import { Card, Badge, Button } from '@shared/components/ui';
import { getStatusTone, toneToBadgeVariant } from '@shared/config/theme';
import { Activity, Server, Database, HardDrive, AlertTriangle, RefreshCw } from 'lucide-react';

interface HealthCheckResult {
  status: string;
  response_time: number;
  message: string;
  details?: Record<string, unknown>;
}

interface ServerHealthStats {
  status: string;
  cpu_usage_percent: number;
  memory_usage_percent: number;
  memory_used_bytes: number;
  memory_total_bytes: number;
  active_connections: number;
  error_rate_percent: number;
  uptime: string;
}

interface ActiveAlert {
  id: string;
  metric_type: string;
  severity: string;
  current_value: number;
  threshold_value: number;
  message: string;
  created_at: string;
}

interface AlertThreshold {
  metric_type: string;
  warning_threshold: number;
  critical_threshold: number;
  enabled: boolean;
}

interface SystemHealthStatus {
  status: string;
  timestamp: string;
  database: HealthCheckResult;
  redis: HealthCheckResult;
  storage: HealthCheckResult;
  server: ServerHealthStats;
  active_alerts: ActiveAlert[];
  alert_thresholds: Record<string, AlertThreshold>;
}

const SystemHealth: React.FC = () => {
  const { t, i18n } = useTranslation('admin');
  const [health, setHealth] = useState<SystemHealthStatus | null>(null);
  // Read inside the adaptive poll's fetcher so the poll is not recreated on
  // every response.
  const healthRef = useRef<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/health');
      setHealth(response.data);
    } catch (err: unknown) {
      setError(t('systemHealth.errors.loadFailed'));
      logger.error(
        'Error fetching health data',
        err instanceof Error ? err : new Error(String(err))
      );
    } finally {
      setLoading(false);
    }
  };

  // Keep the poll's ref pointing at the latest value. The initial load is done
  // by the poll's own first tick, so it is not duplicated here.
  healthRef.current = health ? JSON.stringify(health) : '';

  // Health changes slowly. Adaptive: 30s base backing off to 5min once the
  // payload stops changing, and paused while the tab is hidden.
  useAdaptivePoll({
    enabled: true,
    baseIntervalMs: 30000,
    maxIntervalMs: 300000,
    fetcher: async () => {
      await fetchHealth();
      return healthRef.current;
    },
    isEqual: (a, b) => a === b,
  });

  return (
    <>
      <div className="space-y-8 animate-in fade-in duration-700">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="app-page-title">{t('systemHealth.title')}</h1>
            <p className="text-text-muted-light dark:text-text-muted-dark font-medium mt-1">
              {t('systemHealth.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {health && (
              <Badge
                variant={toneToBadgeVariant[getStatusTone(health.status)]}
                className="h-10 px-4 text-sm"
              >
                {t('systemHealth.overallStatus')}: {translateHealthStatus(health.status, t)}
              </Badge>
            )}
            <Button onClick={fetchHealth} variant="outline" size="md">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />{' '}
              {t('systemHealth.refresh')}
            </Button>
          </div>
        </div>

        {error && (
          <Card className="bg-state-danger-bg-light dark:bg-state-danger-bg-dark border-state-danger-light/20 dark:border-state-danger-dark/20 p-4 flex items-center gap-3 text-state-danger-light dark:text-state-danger-dark ">
            <AlertTriangle className="shrink-0" />
            <p role="alert" className="font-bold">
              {error}
            </p>
          </Card>
        )}

        {health ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <HealthCard
                title={t('systemHealth.components.database')}
                icon={<Database size={24} />}
                status={health.database.status}
                latency={health.database.response_time}
                details={health.database.details}
                t={t}
              />
              <HealthCard
                title={t('systemHealth.components.redis')}
                icon={<Activity size={24} />}
                status={health.redis.status}
                latency={health.redis.response_time}
                t={t}
              />
              <HealthCard
                title={t('systemHealth.components.storage')}
                icon={<HardDrive size={24} />}
                status={health.storage.status}
                latency={health.storage.response_time}
                t={t}
              />
            </div>

            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent-soft-light dark:bg-accent-soft-dark rounded-lg text-accent-text-light dark:text-accent-text-dark">
                  <Server size={24} />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {t('systemHealth.serverResources')}
                </h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <ResourceMetric
                  label={t('systemHealth.cpuUsage')}
                  value={formatPercent(
                    health.server.cpu_usage_percent,
                    i18n.resolvedLanguage || 'en'
                  )}
                  subtext={t('systemHealth.cores', { count: 4 })}
                />
                <ResourceMetric
                  label={t('systemHealth.memory')}
                  value={formatPercent(
                    health.server.memory_usage_percent,
                    i18n.resolvedLanguage || 'en'
                  )}
                  subtext={`${formatBytes(health.server.memory_used_bytes, i18n.resolvedLanguage || 'en')} / ${formatBytes(health.server.memory_total_bytes, i18n.resolvedLanguage || 'en')}`}
                />
                <ResourceMetric
                  label={t('systemHealth.activeConnections')}
                  value={new Intl.NumberFormat(i18n.resolvedLanguage || 'en').format(
                    health.server.active_connections
                  )}
                  subtext={t('systemHealth.webSocketAndHttp')}
                />
                <ResourceMetric
                  label={t('systemHealth.systemUptime')}
                  value={formatUptime(health.server.uptime, i18n.resolvedLanguage || 'en')}
                  subtext={t('systemHealth.sinceLastReboot')}
                />
              </div>
            </Card>

            {health.active_alerts.length > 0 && (
              <Card className="p-6 border-state-danger-light/20 dark:border-state-danger-dark/20 bg-state-danger-bg-light/10 dark:bg-state-danger-bg-dark/10">
                <h3 className="text-lg font-bold text-state-danger-light dark:text-state-danger-dark mb-4 flex items-center gap-2">
                  <AlertTriangle /> {t('systemHealth.activeAlerts')}
                </h3>
                <div className="space-y-3">
                  {health.active_alerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="p-4 bg-surface-card-light dark:bg-surface-card-dark border border-state-danger-light/20 dark:border-state-danger-dark/20 rounded-xl shadow-sm flex justify-between items-center"
                    >
                      <div>
                        <p className="font-bold text-slate-900 dark:text-white">
                          {translateHealthMetric(alert.metric_type, t)}
                        </p>
                        <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                          {alert.message}
                        </p>
                      </div>
                      <Badge variant={toneToBadgeVariant[getStatusTone(alert.severity)]}>
                        {translateHealthSeverity(alert.severity, t)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        ) : !error ? (
          <div className="py-20 text-center">
            <p className="text-text-subtle-light dark:text-text-subtle-dark font-bold uppercase tracking-widest text-xs">
              {t('systemHealth.waitingForData')}
            </p>
          </div>
        ) : null}
      </div>
    </>
  );
};

function HealthCard({ title, icon, status, latency, details, t }: any) {
  return (
    <Card className="p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-surface-2-light dark:bg-surface-2-dark rounded-lg text-text-muted-light dark:text-text-muted-dark">
            {icon}
          </div>
          <h3 className="font-bold text-slate-900 dark:text-white">{title}</h3>
        </div>
        <Badge variant={toneToBadgeVariant[getStatusTone(status)]}>
          {translateHealthStatus(status, t)}
        </Badge>
      </div>
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-text-muted-light dark:text-text-muted-dark">
            {t('systemHealth.responseTime')}
          </span>
          <span className="font-mono font-bold text-slate-900 dark:text-white">{latency}ms</span>
        </div>
        {details &&
          Object.entries(details).map(([key, value]) => (
            <div key={key} className="flex justify-between text-sm">
              <span className="text-text-muted-light dark:text-text-muted-dark">
                {translateHealthDetail(key, t)}
              </span>
              <span className="font-mono font-bold text-slate-900 dark:text-white">
                {String(value)}
              </span>
            </div>
          ))}
      </div>
    </Card>
  );
}

function ResourceMetric({ label, value, subtext }: any) {
  return (
    <div>
      <p className="text-xs font-bold text-text-subtle-light dark:text-text-subtle-dark uppercase tracking-widest mb-1">
        {label}
      </p>
      <p className="text-2xl font-black text-slate-900 dark:text-white">{value}</p>
      <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">{subtext}</p>
    </div>
  );
}

export default SystemHealth;

function translateHealthStatus(status: string, t: (key: string) => string) {
  const key = `systemHealth.status.${status.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function translateHealthDetail(detail: string, t: (key: string) => string) {
  const key = `systemHealth.details.${detail
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')}`;
  const translated = t(key);
  return translated === key
    ? detail.replace(/[_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : translated;
}

function translateHealthSeverity(severity: string, t: (key: string) => string) {
  const key = `systemHealth.severity.${severity.toLowerCase()}`;
  const translated = t(key);
  return translated === key
    ? severity.replace(/[_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : translated;
}

function translateHealthMetric(metric: string, t: (key: string) => string) {
  const key = `systemHealth.metrics.${metric
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')}`;
  const translated = t(key);
  return translated === key
    ? metric.replace(/[_]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : translated;
}

function formatPercent(value: number, locale: string) {
  return `${new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value)}%`;
}

function formatUptime(value: string, locale: string) {
  const matches = [...value.matchAll(/(\d+(?:\.\d+)?)(ms|µs|us|ns|h|m|s)/g)];
  if (matches.length === 0 || matches.map(([segment]) => segment).join('') !== value) return value;

  const multipliers: Record<string, number> = {
    h: 3600,
    m: 60,
    s: 1,
    ms: 0.001,
    us: 0.000001,
    µs: 0.000001,
    ns: 0.000000001,
  };
  const totalSeconds = matches.reduce(
    (total, [, amount, unit]) => total + Number(amount) * multipliers[unit],
    0
  );
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return value;

  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const units: Array<[number, 'day' | 'hour' | 'minute' | 'second']> = days
    ? [[days, 'day'], ...(hours ? [[hours, 'hour'] as [number, 'hour']] : [])]
    : hours
      ? [[hours, 'hour'], ...(minutes ? [[minutes, 'minute'] as [number, 'minute']] : [])]
      : minutes
        ? [[minutes, 'minute'], ...(seconds ? [[seconds, 'second'] as [number, 'second']] : [])]
        : [[seconds, 'second']];

  return units
    .map(([count, unit]) =>
      new Intl.NumberFormat(locale, {
        style: 'unit',
        unit,
        unitDisplay: 'short',
        maximumFractionDigits: 0,
      }).format(count)
    )
    .join(' ');
}
